<template>
  <div class="category-block">
    <div class="logo-header">
      <div class="logo-title-area">
        <div class="section-title">■ ADD IMAGE</div>
        <span class="drag-hint-text" :class="{ 'hide-on-mobile': true }">Drag & drop images into the 3D model</span>
      </div>
      <button class="upload-btn" @click="triggerFileUpload">Upload image</button>
    </div>
    <div class="layer-list" v-if="logoLayers.length > 0">
      <div 
        v-for="item in logoLayers" 
        :key="item.id"
        class="layer-item"
        :class="{ 'editing': currentEditingLogo && currentEditingLogo.id === item.id }"
        @click="selectLogoLayer(item)"
      >
        <img class="layer-thumb" :src="item.url">
        <div class="layer-info">{{ item.part }}</div>
        <button class="icon-btn edit-btn edit-btn-blue" @click.stop="editLogoLayer(item)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 3l4 4L7 21H3v-4L17 3z"/>
            <path d="M15 5l4 4"/>
          </svg>
        </button>
      </div>
    </div>
    <div class="image-gallery-grid" ref="galleryContainer">
      <div 
        v-for="item in imageGallery" 
        :key="item.id"
        class="gallery-image-item"
        :data-image-id="item.id"
        :data-image-url="item.url"
        draggable="true"
        @dragstart="onDragStart($event, item)"
        @dragend="onDragEnd"
      >
        <img 
          loading="lazy"
          :src="item.url" 
          style="width:100%; height:100%; object-fit: contain; pointer-events: none;">
      </div>
      <div v-if="imageGallery.length === 0" class="gallery-empty">
        No logos yet. Upload your files to start.<br>Drag and drop images onto the 3D model.
      </div>
    </div>
    <input type="file" ref="fileInput" accept="image/*" class="hidden-file-input" multiple @change="onFileSelect">
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useDesignStore } from '@/stores/designStore'

export default {
  name: 'LogoUploader',
  props: {
    currentSelectedPart: {
      type: String,
      default: 'front'
    }
  },
  computed: {
    logoLayers() {
      const store = useDesignStore()
      const allLogos = []
      for (const [part, layers] of store.partLayersMap.entries()) {
        layers.forEach(layer => {
          if (!layer.isText && layer.url) {
            allLogos.push({
              id: layer.id,
              url: layer.url,
              part: part,
              layer: layer
            })
          }
        })
      }
      return allLogos
    },
    currentEditingLogo() {
      const store = useDesignStore()
      return store.currentEditingLogo
    }
  },
  data() {
    return {
      imageGallery: []
    }
  },
  
  mounted() {
    this.setupDragAndDrop()
    this.loadImageGallery()
  },
  methods: {
    ...mapActions(useDesignStore, ['addImageToPart']),
    
    selectLogoLayer(item) {
      const store = useDesignStore()
      store.selectLogo(item.part, item.id, false)
      if (window.$viewer3D) {
        window.$viewer3D.compositePartTexture(item.part, item.id)
        window.$viewer3D.showTextureControls()
      }
    },

    editLogoLayer(item) {
      const store = useDesignStore()
      store.selectLogo(item.part, item.id, false)
      if (window.$viewer3D) {
        window.$viewer3D.compositePartTexture(item.part, item.id)
        window.$viewer3D.showTextureControls()
      }
    },

    triggerFileUpload() {
      this.$refs.fileInput.click()
    },
    
    async onFileSelect(e) {
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml']
      const maxSize = 5 * 1024 * 1024
      
      for (const file of Array.from(e.target.files)) {
        if (!allowedTypes.includes(file.type)) {
          alert(`File "${file.name}" is not supported. Please upload PNG, JPG, WebP, or SVG.`)
          continue
        }
        if (file.size > maxSize) {
          alert(`File "${file.name}" is too large. Maximum size is 5MB.`)
          continue
        }
        const serverUrl = await this.uploadImageToServer(file)
        if (serverUrl) {
          if (!this.imageGallery.some(item => item.url === serverUrl)) {
            this.imageGallery.push({ id: Date.now() + Math.random() * 10000, url: serverUrl })
            this.saveImageGallery()
          }
          this.$emit('add-image', { url: serverUrl, part: this.currentSelectedPart, imageId: null })
        }
      }
      e.target.value = ''
    },
    
    async uploadImageToServer(file) {
      const formData = new FormData()
      formData.append('image', file)
      try {
        const response = await fetch('/upload-image.php', {
          method: 'POST',
          body: formData
        })
        const result = await response.json()
        if (result.success) {
          return result.url
        }
      } catch (e) {
        console.error('Image upload failed:', e)
      }
      return null
    },
    
    onDragStart(e, item) {
      e.dataTransfer.setData('text/plain', JSON.stringify({
        type: 'gallery-image',
        url: item.url,
        imageId: item.id
      }))
      e.dataTransfer.effectAllowed = 'copy'
      e.target.closest('.gallery-image-item').style.opacity = '0.5'
    },
    
    onDragEnd(e) {
      if (e.target.closest('.gallery-image-item')) {
        e.target.closest('.gallery-image-item').style.opacity = '1'
      }
    },
    
    setupDragAndDrop() {
      const container = this.$refs.galleryContainer
      if (!container) return
      
      container.addEventListener('dragover', (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      })
    },
    
loadImageGallery() {
      localStorage.removeItem('imageGallery')
      const store = useDesignStore()
      const designId = store.currentDesignId
      if (designId) {
        const saved = localStorage.getItem('imageGallery_' + designId)
        if (saved) {
          try {
            this.imageGallery = JSON.parse(saved)
          } catch (e) {
            this.imageGallery = []
          }
        }
      } else {
        this.imageGallery = []
      }
    },
    
    saveImageGallery() {
      // 只在有 designId 时才持久化
      const store = useDesignStore()
      const designId = store.currentDesignId
      if (designId) {
        localStorage.setItem('imageGallery_' + designId, JSON.stringify(this.imageGallery))
      }
      // 没有 designId 时只保留在内存中，关闭浏览器后自动清除
    },
    
    addImageToGallery(url, id) {
      if (!this.imageGallery.some(item => item.url === url)) {
        this.imageGallery.push({ id: id || Date.now() + Math.random() * 10000, url: url })
        this.saveImageGallery()
      }
    }
  }
}
</script>

<style scoped>
.image-gallery-grid {
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 4px;
  margin-top: 10px;
  max-height: 180px;
  overflow-y: auto;
  padding: 6px;
  background: transparent;
  border-radius: 8px;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.gallery-image-item {
  aspect-ratio: 1 / 1;
  background: #f8f9fa;
  border-radius: 4px;
  cursor: grab;
  transition: all 0.2s ease;
  border: 1px solid #e0e0e0;
  overflow: hidden;
  position: relative;
}

.gallery-image-item:active {
  cursor: grabbing;
}

.gallery-image-item:hover {
  transform: scale(1.05);
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.upload-btn {
  background: #e0e7ff;
  border: 1px solid #c7d2fe;
  color: #3730a3;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.75rem;
  cursor: pointer;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #072a47;
  margin-bottom: 0.3rem;
  padding-left: 4px;
}

.category-block {
  margin-bottom: 0.6rem;
  border: none;
  position: relative;
}

.layer-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
  padding: 4px;
}

.layer-item {
  background: #ffffff;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border: 1px solid #e0e0e0;
  cursor: pointer;
}

.layer-item.editing {
  background: #f0f0f0;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
}

.layer-thumb {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 8px;
}

.layer-info {
  flex: 1;
  font-size: 0.7rem;
}

.icon-btn {
  background: #2d3748;
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.logo-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.logo-title-area {
  display: flex;
  align-items: center;
  gap: 12px;
}

.drag-hint-text {
  font-size: 0.7rem;
  color: #999;
}

.layer-list {
  margin-top: 8px;
  margin-bottom: 8px;
}

.edit-btn-blue {
  background: #3b82f6;
}

.logo-layer-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
}

.gallery-empty {
  font-size: 0.7rem;
  color: #666;
  text-align: center;
  grid-column: 1 / -1;
}

.hidden-file-input {
  display: none;
}

@media (max-width: 850px) {
  .image-gallery-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
    max-height: 150px;
  }
  .drag-hint-text.hide-on-mobile {
    display: none;
  }
}
</style>